// Back-end DTI for the loan file header. A stored `loan.dti` (a percentage; Arive's
// feed carries it) wins. Without one, estimate it from the 1003: the proposed
// payment plus the monthly liabilities that stay after closing, over the monthly
// income the borrowers have now. A liability flagged "paid off at closing" drops
// out, and past jobs never count. Taxes, insurance and HOA on a property the borrower
// keeps count too. Nothing is guessed: no income or no loan amount
// is no figure, and neither is a missing rate, property tax or homeowners insurance.
import { proposedPayment } from './proposedPayment';

const num = (v) => Number(v) || 0;
const round1 = (v) => Math.round(v * 10) / 10;

export function loanDti(loan) {
  const stored = num(loan?.dti);
  if (stored > 0) return { value: round1(stored), source: 'stored' };

  const urla = loan?.urla || {};
  const income = [...(urla.current_employment || []), ...(urla.other_income || [])]
    .filter((r) => r.is_current !== false)
    .reduce((s, r) => s + num(r.monthly_gross), 0);
  // Qualify on the qualifying rate when there is one, else the note rate, else the locked one.
  // Only a positive rate counts, so a bad qualifying rate falls through.
  const rate = [loan?.qualifying_rate, loan?.note_rate, loan?.rate_lock?.rate].map(num).find((v) => v > 0) || 0;
  if (income <= 0 || num(loan?.loan_amount) <= 0 || rate <= 0) return { value: null, source: null };

  const debts = (urla.liabilities || [])
    .filter((l) => !l.to_be_paid_off)
    .reduce((s, l) => s + num(l.monthly_payment), 0);
  // DECIDE:b8f1b0f4-c9a6-413e-9b81-0e6af3825490 (rent credit) Kept properties: their taxes/insurance/HOA live only on the REO row. The mortgage
  // is expected on the liabilities list, so it is not added again, and no rental income
  // is credited against them. On a refinance the retained primary is the subject home,
  // whose taxes and insurance already come from the LE fees.
  const refi = ['rate_term_refi', 'cash_out_refi'].includes(loan?.loan_purpose);
  // DECIDE:6e3af2e0-5adb-4c6d-b0a1-ff04d7ec33b9 (which row is the refinanced home)
  const notKept = refi ? ['sold', 'pending_sale', 'retained_primary'] : ['sold', 'pending_sale'];
  const keptReo = (urla.reo_properties || [])
    .filter((r) => !notKept.includes(r?.intended_status))
    .reduce((s, r) => s + num(r?.monthly_taxes_ins_hoa), 0);
  const payment = proposedPayment({ ...loan, note_rate: rate, other_new_mortgages: urla.other_new_mortgages });
  // DECIDE:88d430b7-bb36-42dc-a892-f09a1ba9fa34 Without taxes and insurance the housing payment is unknown, so no estimate.
  if (payment.taxes <= 0 || payment.hoi <= 0) return { value: null, source: null };
  return { value: round1(((payment.total + debts + keptReo) / income) * 100), source: 'estimate' };
}
